import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, ArrowLeft, Sparkles, Star, Lock, Eye } from "lucide-react";
import { useList } from "./AdminApp";
import { PageTitle } from "./Layout";
import { useCrud } from "../../lib/adminApi";
import { tr, fmtDate } from "../../lib/i18n";
import { img } from "../../lib/api";
import { autoTags } from "../../lib/text";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Field, I18nInput, ImageUpload, SlugField, SeoFields } from "../../components/admin/Fields";
import { Switch } from "../../components/ui/switch";
import RichEditor from "../../components/admin/RichEditor";
import TrainingsManager from "../../components/admin/TrainingsManager";
import PressManager, { PressSearchPanel } from "../../components/admin/PressManager";
import SortableList from "../../components/admin/SortableList";
import AspectCropUpload from "../../components/admin/AspectCropUpload";

const LOCKED_PAGES = new Set(["ben-kimim", "vizyonum", "egitimlerim", "basin-arsivi"]);

const StatusSelect = ({ value, onChange, testid }) => (
  <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={value || "draft"} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    <option value="draft">Taslak</option>
    <option value="published">Yayında</option>
  </select>
);

const SCOPES = [["both", "Ortak (iki temada)"], ["normal", "Sadece Normal tema"], ["adaylik", "Sadece Adaylık teması"]];
const scopeLabel = (v) => (SCOPES.find(([k]) => k === (v || "both")) || SCOPES[0])[1];

function Editor({ col, item, onBack, isNews }) {
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  const { save } = useCrud(col);
  const { data: cats = [] } = useList("news_categories");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const prefix = isNews ? "/yazilar/" : "/";
  const isEgitim = !isNews && (f.slug === "egitimlerim" || item.slug === "egitimlerim");
  const isBasin = !isNews && (f.slug === "basin-arsivi" || item.slug === "basin-arsivi");
  const isBenkimim = !isNews && (f.slug === "ben-kimim" || item.slug === "ben-kimim");
  const lastAutoTags = useRef(JSON.stringify(item.tags || []));
  const tagsKey = JSON.stringify([f.title, f.summary, f.body]);
  useEffect(() => {
    if (!isNews) return;
    const cur = f.tags || [];
    if (cur.length && JSON.stringify(cur) !== lastAutoTags.current) return; // kullanıcı elle düzenlemiş
    const gen = autoTags(f.title, f.body, f.summary);
    if (gen.length && JSON.stringify(gen) !== JSON.stringify(cur)) {
      lastAutoTags.current = JSON.stringify(gen);
      setF((p) => ({ ...p, tags: gen }));
    }
  }, [tagsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const regenTags = () => {
    const gen = autoTags(f.title, f.body, f.summary);
    if (!gen.length) return toast.error("Önce başlık ve içerik yazın");
    lastAutoTags.current = JSON.stringify(gen);
    setF((p) => ({ ...p, tags: gen }));
  };
  const submit = async (e) => {
    e.preventDefault();
    // /yazilar/* yayınına giden News yazıları için hem kapak (1200×630) hem
    // hikaye (1080×1920) görseli zorunlu — sosyal paylaşımın her iki
    // formatında da düzgün bir önizleme çıksın.
    if (isNews) {
      if (!f.cover) { toast.error("Kapak görseli (1200×630) zorunludur"); return; }
      if (!f.story) { toast.error("Hikaye görseli (1080×1920) zorunludur"); return; }
    }
    setBusy(true);
    const r = await save({ ...f, slug: f.slug ?? "" });
    setBusy(false);
    if (r) setF(r);
    if (r?.copy_created) toast.success("Diğer tema için taslak kopya oluşturuldu. Yayınladıktan sonra Menü bölümünden menüye eklemeyi unutmayın.", { duration: 7000 });
  };
  return (
    <>
    <form onSubmit={submit} data-testid={`${col}-editor`}>
      <PageTitle title={item.id ? "Düzenle" : isNews ? "Yeni yazı" : "Yeni sayfa"}
        action={<div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={onBack} data-testid="editor-back"><ArrowLeft size={15} className="mr-1" />Liste</Button>
          {f.id && f.slug && <Button type="button" variant="outline" asChild data-testid="editor-live-preview"><a href={`/admin/onizleme?path=${encodeURIComponent(`${prefix}${f.slug}`)}&title=${encodeURIComponent(f.title?.tr || f.slug)}&back=${encodeURIComponent(isNews ? "/admin/haberler" : "/admin/sayfalar")}`} target="_blank" rel="noreferrer"><Eye size={15} className="mr-1" />Canlı önizleme</a></Button>}
          {f.id && f.status === "published" && <Button type="button" variant="outline" asChild><a href={`${prefix}${f.slug}`} target="_blank" rel="noreferrer"><ExternalLink size={15} className="mr-1" />Gör</a></Button>}
          <Button disabled={busy} data-testid="editor-save">{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
        </div>} />
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-5 bg-white border rounded-xl p-5">
          <I18nInput label="Başlık" value={f.title} onChange={set("title")} testid="editor-title" maxLength={200} />
          {isNews && <I18nInput label="Özet" value={f.summary} onChange={set("summary")} multiline testid="editor-summary" maxLength={600} />}
          {!isBasin && <RichEditor label="İçerik" value={f.body} onChange={set("body")} testid="editor-body" />}
          {isBasin && <PressSearchPanel compact />}
        </div>
        <div className="space-y-5">
          <div className="bg-white border rounded-xl p-5 space-y-4">
            <Field label="Durum"><StatusSelect value={f.status} onChange={set("status")} testid="editor-status" /></Field>
            {!isNews && !isBasin && (
              <Field label="Hangi temada yayınlansın?" hint={f.id && (item.theme_scope || "both") === "both" && f.theme_scope && f.theme_scope !== "both" ? "Kaydedince diğer tema için içeriği kopyalanmış bir taslak oluşturulur." : undefined}>
                <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={f.theme_scope || "both"} onChange={(e) => set("theme_scope")(e.target.value)} data-testid="editor-theme-scope">
                  {SCOPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            )}
            {!isBasin && <SlugField value={f.slug} onChange={set("slug")} source={f.title?.tr} prefix={prefix} testid="editor-slug" />}
            {isEgitim && (
              <Field label="Eğitim bölümleri" hint="Sayfada hangi bölümler görünsün?">
                <label className="flex items-center gap-3 text-sm text-stone-600 mb-2"><Switch checked={f.trainings_show_received !== false} onCheckedChange={set("trainings_show_received")} data-testid="editor-show-received" />Aldığım eğitimler bölümü</label>
                <label className="flex items-center gap-3 text-sm text-stone-600"><Switch checked={f.trainings_show_given !== false} onCheckedChange={set("trainings_show_given")} data-testid="editor-show-given" />Verdiğim eğitimler bölümü</label>
              </Field>
            )}
            {isNews && (
              <>
                <Field label="Kategori">
                  <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={f.category_id || ""} onChange={(e) => set("category_id")(e.target.value || null)} data-testid="editor-category">
                    <option value="">— Yok —</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{tr(c.name)}</option>)}
                  </select>
                </Field>
                <Field label="Etiketler" hint="Başlık ve içerikten otomatik oluşturulur; virgülle ayırarak düzenleyebilirsiniz.">
                  <div className="flex justify-end -mt-1 mb-1">
                    <button type="button" onClick={regenTags} className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1" data-testid="editor-tags-auto"><Sparkles size={12} />Otomatik oluştur</button>
                  </div>
                  <Input value={(f.tags || []).join(", ")} onChange={(e) => { lastAutoTags.current = null; set("tags")(e.target.value.split(",").map((s) => s.trim()).filter(Boolean)); }} data-testid="editor-tags" />
                </Field>
                <Field label="Yayın tarihi">
                  <Input type="datetime-local" value={(f.published_at || "").slice(0, 16)} onChange={(e) => set("published_at")(e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="editor-date" />
                </Field>
                <Field label="Anasayfada öne çıkan" hint="Açıksa bu yazı anasayfada büyük kapaklı kartta gösterilir. Aynı anda yalnızca bir yazı öne çıkabilir.">
                  <label className="flex items-center gap-3 text-sm text-stone-600">
                    <Switch checked={!!f.featured} onCheckedChange={set("featured")} data-testid="editor-featured" />
                    Büyük kartta göster
                  </label>
                </Field>
              </>
            )}
            {!isBasin && <ImageUpload label={isBenkimim ? "Fotoğraf" : isNews ? "Kapak görseli (1200×630) *" : "Kapak görseli"} value={f.cover} onChange={set("cover")} testid="editor-cover" alt={f.title?.tr} />}
            {!isBasin && !isBenkimim && <p className="text-xs text-stone-400 -mt-2">{isNews ? "Zorunlu · /yazilar paylaşımında Facebook/WhatsApp/Twitter önizleme kartı olarak kullanılır." : "Bu görsel aynı zamanda sosyal paylaşım (Facebook, WhatsApp, Twitter) önizleme kartı olarak kullanılır. SEO sekmesinden ayrı bir OG görseli de belirleyebilirsiniz."}</p>}
            {isNews && (
              <AspectCropUpload
                label="Hikaye görseli (Instagram Story) *"
                hint="Zorunlu · 1080×1920 oranına kırpılır, orijinal çözünürlük korunur. Instagram/Facebook hikaye paylaşımları için dikey görsel."
                value={f.story || ""}
                onChange={set("story")}
                aspect={1080 / 1920}
                aspectLabel="1080×1920"
                testid="editor-story"
                previewClass="w-28 h-48 rounded-lg mx-auto"
                accent="pink"
                required
              />
            )}
            {isBenkimim && <p className="text-xs text-stone-400 -mt-2">Ben Kimim sayfasında yanda görünür. Boşsa profil fotoğrafı kullanılır.</p>}
            {isBenkimim && (
              <Field label="Fotoğraf hareketi" hint="Fareyle üzerine gelince hafif büyüme/hareket efekti.">
                <label className="flex items-center gap-3 text-sm text-stone-600"><Switch checked={f.benkimim_photo_hover !== false} onCheckedChange={set("benkimim_photo_hover")} data-testid="editor-benkimim-hover" />Hareket efekti açık</label>
              </Field>
            )}
          </div>
          <SeoFields value={f.seo} onChange={set("seo")} title={f.title} body={isNews ? (f.summary?.tr ? f.summary : f.body) : f.body} path={`${prefix}${f.slug || ""}`} />
        </div>
      </div>
    </form>
    {isEgitim && <TrainingsManager />}
    {isBasin && <PressManager />}
    </>
  );
}

function NewsCategories() {
  const { data = [] } = useList("news_categories");
  const { save, remove } = useCrud("news_categories");
  const [name, setName] = useState("");
  return (
    <div className="bg-white border rounded-xl p-5 mt-8" data-testid="news-categories-manager">
      <p className="font-semibold mb-3">Yazı kategorileri</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {data.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-2 border rounded-full pl-3 pr-1 py-1 text-sm">
            {tr(c.name)} <code className="text-[10px] text-stone-400">/{c.slug}</code>
            <button onClick={() => remove(c.id, "Kategori")} className="p-1 text-stone-400 hover:text-red-600" data-testid={`delete-news-cat-${c.slug}`}><Trash2 size={13} /></button>
          </span>
        ))}
      </div>
      <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (name && (await save({ name: { tr: name } }))) setName(""); }}>
        <Input placeholder="Yeni kategori adı" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-news-cat-input" />
        <Button data-testid="add-news-cat-button"><Plus size={15} /></Button>
      </form>
    </div>
  );
}

function ContentAdmin({ col, isNews }) {
  const { data = [], isLoading } = useList(col);
  const { remove, reorder } = useCrud(col);
  const [editing, setEditing] = useState(null);
  if (editing) return <Editor col={col} item={editing} isNews={isNews} onBack={() => setEditing(null)} />;
  const blank = { title: { tr: "" }, body: { tr: "" }, status: "draft", seo: {}, ...(isNews && { summary: { tr: "" }, tags: [] }) };
  const row = (it) => (
    <div className="flex items-center gap-4 py-1" data-testid={`${col}-row-${it.slug}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{tr(it.title) || "(başlıksız)"}</p>
        <p className="text-xs text-stone-400">{isNews ? "/yazilar/" : "/"}{it.slug}{isNews && ` · ${fmtDate(it.published_at, "tr")}`}</p>
      </div>
      {!isNews && <Badge variant="outline" data-testid={`scope-badge-${it.slug}`}>{scopeLabel(it.theme_scope)}</Badge>}
      {!isNews && LOCKED_PAGES.has(it.slug) && <Badge className="bg-stone-200 text-stone-700 hover:bg-stone-200" data-testid={`locked-badge-${it.slug}`}><Lock size={11} className="mr-1" />Sabit</Badge>}
      {isNews && it.featured && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`featured-badge-${it.slug}`}>Öne çıkan</Badge>}
      <Badge variant={it.status === "published" ? "default" : "secondary"}>{it.status === "published" ? "Yayında" : "Taslak"}</Badge>
      {col === "pages" && it.slug === "basin-arsivi" ? (
        <Button size="sm" variant="outline" onClick={() => setEditing(it)} data-testid={`edit-${col}-${it.slug}`}><Eye size={14} className="mr-1" />Göster</Button>
      ) : (
        <Button size="icon" variant="ghost" onClick={() => setEditing(it)} data-testid={`edit-${col}-${it.slug}`}><Pencil size={15} /></Button>
      )}
      {!(col === "pages" && LOCKED_PAGES.has(it.slug)) && <Button size="icon" variant="ghost" onClick={() => remove(it.id, tr(it.title))} data-testid={`delete-${col}-${it.slug}`}><Trash2 size={15} className="text-red-500" /></Button>}
    </div>
  );
  return (
    <>
      <PageTitle title={isNews ? "Yazılar" : "Sayfalar"} sub={isNews ? "Yazı ekleyin, düzenleyin, yayından kaldırın. Sol kenardaki tutamacı sürükleyerek sıralayın." : "Her sayfa kendi adresiyle yayınlanır; menüye Menü bölümünden ekleyin."}
        action={<Button onClick={() => setEditing(blank)} data-testid={`new-${col}-button`}><Plus size={15} className="mr-1" />{isNews ? "Yeni yazı" : "Yeni sayfa"}</Button>} />
      {isNews && (() => {
        const feat = data.find((d) => d.featured);
        const eff = feat || data.find((d) => d.cover && d.status === "published");
        if (!eff) return null;
        return (
          <div className="mb-4 flex items-center gap-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="featured-preview-card">
            <Star size={18} className="text-amber-500 shrink-0" fill="currentColor" />
            {eff.cover && <img src={img(eff.cover)} alt="" loading="lazy" decoding="async" className="w-20 h-14 object-cover rounded-md shrink-0 border border-amber-200" />}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">{feat ? "Anasayfada öne çıkan yazı" : "Öne çıkan (otomatik: son kapaklı yazı)"}</p>
              <p className="font-medium truncate text-stone-800">{tr(eff.title) || "(başlıksız)"}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(eff)} data-testid="featured-preview-edit">Düzenle</Button>
          </div>
        );
      })()}
      {isLoading && <p className="p-6 text-sm text-stone-400">Yükleniyor…</p>}
      {!isLoading && !data.length && <p className="p-6 text-sm text-stone-400 bg-white border rounded-xl">Henüz içerik yok.</p>}
      {!!data.length && isNews && (
        <div data-testid={`${col}-list`}>
          <SortableList items={data} onReorder={reorder} render={row} />
        </div>
      )}
      {!!data.length && !isNews && (
        <div className="bg-white border rounded-xl divide-y" data-testid={`${col}-list`}>
          {data.map((it) => (
            <div key={it.id} className="px-5 py-3.5">{row(it)}</div>
          ))}
        </div>
      )}
      {isNews && <NewsCategories />}
    </>
  );
}

export const PagesAdmin = () => <ContentAdmin col="pages" />;
export const NewsAdmin = () => <ContentAdmin col="news" isNews />;
